import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Rocket, Award, Shield, TrendingUp, Users, CheckCircle, Mail, Loader2, Calendar } from 'lucide-react';
import { config } from '@/lib/config';

// Investor form data interface
interface InvestorFormData {
  name: string;
  firm: string;
  email: string;
  whyNeurosEdge: string;
  ndaRequired: boolean;
}

export function InvestorSection() {
  const { toast } = useToast();
  const [investorLoading, setInvestorLoading] = useState(false);

  const [investorFormData, setInvestorFormData] = useState<InvestorFormData>({
    name: '',
    firm: '',
    email: '',
    whyNeurosEdge: '',
    ndaRequired: false
  });

  const handleInvestorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!investorFormData.name || !investorFormData.firm || !investorFormData.email || !investorFormData.whyNeurosEdge) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    setInvestorLoading(true);

    try {
      const response = await fetch(`${config.api.baseUrl}${config.api.endpoints.investor}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(investorFormData),
      });

      const result = await response.json();

      if (result.success) {
        toast({
          title: "Success!",
          description: "✅ Your investor inquiry has been submitted successfully! We'll send you the deck shortly.",
          variant: "default"
        });

        // Reset form
        setInvestorFormData({
          name: '',
          firm: '',
          email: '',
          whyNeurosEdge: '',
          ndaRequired: false
        });
      } else {
        toast({
          title: "Error",
          description: result.message || "❌ Failed to submit inquiry. Please try again later.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error submitting investor form:', error);
      toast({
        title: "Error",
        description: "❌ Failed to submit inquiry. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setInvestorLoading(false);
    }
  };

  return (
    <section className="py-24 bg-gradient-to-b from-muted/20 to-background">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Investor Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-accent to-primary bg-clip-text text-transparent leading-tight pb-2">
              Fuel the Future of Secure AI Execution
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Join us as we turn intent into action for the modern enterprise.
            </p>
            <Button size="lg" className="bg-gradient-to-r from-accent to-primary hover:from-accent/90 hover:to-primary/90">
              <Rocket className="w-5 h-5 mr-2" />
              Request Investor Deck
            </Button>
          </div>

          {/* Why Invest */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold mb-8 text-center">Why Invest</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="gradient-card rounded-xl p-6 text-center">
                <Award className="w-12 h-12 text-accent mx-auto mb-4" />
                <h4 className="font-semibold mb-2">First-Mover Advantage</h4>
                <p className="text-muted-foreground text-sm">Establishing the new "Execution Layer"</p>
              </div>
              <div className="gradient-card rounded-xl p-6 text-center">
                <Shield className="w-12 h-12 text-primary mx-auto mb-4" />
                <h4 className="font-semibold mb-2">Patent-Backed Moat</h4>
                <p className="text-muted-foreground text-sm">Cryptographic guardrails unique in market</p>
              </div>
              <div className="gradient-card rounded-xl p-6 text-center">
                <TrendingUp className="w-12 h-12 text-accent mx-auto mb-4" />
                <h4 className="font-semibold mb-2">Clear Path to Scale</h4>
                <p className="text-muted-foreground text-sm">SaaS + SDK ecosystem for exponential connector growth</p>
              </div>
              <div className="gradient-card rounded-xl p-6 text-center">
                <Users className="w-12 h-12 text-primary mx-auto mb-4" />
                <h4 className="font-semibold mb-2">Seasoned Operator Team</h4>
                <p className="text-muted-foreground text-sm">Decades in enterprise IT automation & AI safety</p>
              </div>
            </div>
          </div>

          {/* Ideal Investor Profile */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold mb-8 text-center">Ideal Investor Profile</h3>
            <div className="gradient-card rounded-xl p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
                    <p>Focus on Enterprise SaaS or AI infrastructure</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
                    <p>Strong Fortune 1000 / federal sectors</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
                    <p>Track record in scaling platform plays (SDK / marketplace)</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
                    <p>Aligned with responsible AI principles</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Secure Contact Form */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold mb-8 text-center">Secure Contact Form</h3>
            <div className="gradient-card rounded-2xl p-8 max-w-2xl mx-auto">
              <form onSubmit={handleInvestorSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Name *</label>
                    <Input
                      name="name"
                      value={investorFormData.name}
                      onChange={(e) => setInvestorFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Your full name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Firm *</label>
                    <Input
                      name="firm"
                      value={investorFormData.firm}
                      onChange={(e) => setInvestorFormData(prev => ({ ...prev, firm: e.target.value }))}
                      placeholder="Investment firm name"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Email *</label>
                  <Input
                    type="email"
                    name="email"
                    value={investorFormData.email}
                    onChange={(e) => setInvestorFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="your.email@firm.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Why NeurosEdge? *</label>
                  <Textarea
                    name="whyNeurosEdge"
                    value={investorFormData.whyNeurosEdge}
                    onChange={(e) => setInvestorFormData(prev => ({ ...prev, whyNeurosEdge: e.target.value }))}
                    placeholder="What interests you about NeurosEdge and our market opportunity?"
                    rows={4}
                    required
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="ndaRequired"
                    checked={investorFormData.ndaRequired}
                    onChange={(e) => setInvestorFormData(prev => ({ ...prev, ndaRequired: e.target.checked }))}
                    className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary"
                  />
                  <label htmlFor="ndaRequired" className="text-sm font-medium">NDA Required?</label>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={investorLoading}
                  className="w-full bg-gradient-to-r from-accent to-primary hover:from-accent/90 hover:to-primary/90"
                >
                  {investorLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Mail className="w-5 h-5 mr-2" />
                      Submit Investor Inquiry
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>

          {/* Events */}
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-8">Upcoming Events</h3>
            <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              <div className="gradient-card rounded-xl p-6">
                <Calendar className="w-8 h-8 text-primary mx-auto mb-3" />
                <h4 className="font-semibold mb-2">Investor Webinar</h4>
                <p className="text-muted-foreground text-sm">Aug 2025</p>
              </div>
              <div className="gradient-card rounded-xl p-6">
                <Calendar className="w-8 h-8 text-accent mx-auto mb-3" />
                <h4 className="font-semibold mb-2">Demo‑day @ SaaStr Annual</h4>
                <p className="text-muted-foreground text-sm">Sep 2025</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
